//-------MUI-----------//
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
} from "@mui/material";
import { styled, ThemeProvider } from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import { useLanguage } from "../../context/LanguageContext";

//-------ICONS-----------//
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import BrightnessMediumIcon from "@mui/icons-material/BrightnessMedium";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import GTranslateIcon from "@mui/icons-material/GTranslate";

//-------THEME-----------//
import { createAppTheme } from "../../theme";
import DivMain from "./DivMain";
//-------- AOS -----------//
import AOS from "aos";
import "aos/dist/aos.css";
//----------------------------
//----------------------------
const drawerWidth = 180;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme }) => ({
    flexGrow: 1,
    padding: theme.spacing(2),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    variants: [
      {
        props: ({ open }) => open,
        style: {
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginLeft: 0,
        },
      },
    ],
  })
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(["margin", "width"], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

function App() {
  const { language, toggleLanguage } = useLanguage();
  const [scroll, setScroll] = useState(null);
  const [open, setOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const translations = {
    es: {
      home: "HERNAN",
      about: "Sobre mí",
      portfolio: "Portafolio",
      technologies: "Tecnologías",
      education: "Educación",
      contact: "Contacto",
    },
    en: {
      home: "HERNAN",
      about: "About Me",
      portfolio: "Portfolio",
      technologies: "Technologies",
      education: "Education",
      contact: "Contact",
    },
  };
  const menuItems = [
    { text: translations[language].home, icon: <HomeIcon />, scrollTo: "top" },
    {
      text: translations[language].about,
      icon: <PersonIcon />,
      scrollTo: "sobreMi",
    },
    {
      text: translations[language].portfolio,
      icon: <WorkIcon />,
      scrollTo: "portafolio",
    },
    {
      text: translations[language].technologies,
      icon: <CodeIcon />,
      scrollTo: "tecnologias",
    },
    {
      text: translations[language].education,
      icon: <SchoolIcon />,
      scrollTo: "educacion",
    },
    {
      text: translations[language].contact,
      icon: <ContactMailIcon />,
      scrollTo: "contacto",
    },
  ];
  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  const handleThemeToggle = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.setAttribute(
      "data-theme",
      !isDarkMode ? "dark" : "light"
    );
  };

  const mainRef = useRef(null);

  const theme = createAppTheme(isDarkMode ? "dark" : "light");

  // Establecer tema inicial
  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      isDarkMode ? "dark" : "light"
    );
  }, [isDarkMode]);

  useEffect(() => {
    const bodyElement = document.body;

    if (isDarkMode) {
      bodyElement.classList.add("dark-theme");
    } else {
      bodyElement.classList.remove("dark-theme");
    }
    return () => {
      bodyElement.classList.remove("dark-theme");
    };
  }, [isDarkMode]);

  //--------useEffect AOS-----------//

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: false,
    });

    let lastScroll = 0;
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      if (Math.abs(currentScroll - lastScroll) > 5) {
        AOS.refresh();
        lastScroll = currentScroll;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  //--------useEffect AOS-----------//
  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          background: isDarkMode
            ? "radial-gradient(circle at center, #0b7465 0%, #062329 80%)"
            : "radial-gradient(circle at center, #ffffff 0%, #96b795 80%)",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          padding: "0 1rem",
          "::before": {
            content: '""',
            position: "fixed",
            width: "300px",
            height: "300px",
            top: "10%",
            left: "10%",
            background: "#0b7465",
            opacity: 0.06,
            borderRadius: "50%",
            filter: "blur(100px)",
            zIndex: -1,
          },
          "::after": {
            content: '""',
            position: "absolute",
            width: "400px",
            height: "400px",
            bottom: "10%",
            right: "5%",
            background: "#0b7465",
            opacity: 0.05,
            borderRadius: "50%",
            filter: "blur(120px)",
            zIndex: 0,
          },
          "&::before, &::after": {
            pointerEvents: "none",
          },
          "&::selection": {
            content: '""',
            position: "absolute",
            width: "300px",
            height: "300px",
            top: "0%",
            right: "10%",
            background: "#0b7465",
            opacity: 0.03,
            borderRadius: "40%",
            filter: "blur(90px)",
            zIndex: 0,
          },
        }}
      >
        <CssBaseline />
        <AppBar position="fixed" open={open}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={[
                {
                  color: theme.palette.text.secondary,
                  mr: 4,
                },
                open && { display: "none" },
              ]}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                flexGrow: 1,
                fontFamily: theme.typography.fontFamily,
                color: theme.palette.text.secondary,
                fontWeight: 600,
              }}
            >
              Hernan Dario Gomez M.
            </Typography>
            <IconButton
              color="inherit"
              sx={{
                color: theme.palette.text.secondary,
              }}
              onClick={handleThemeToggle}
            >
              {isDarkMode ? <BrightnessMediumIcon /> : <DarkModeIcon />}
            </IconButton>
            <IconButton
              sx={{
                color: theme.palette.text.secondary,
              }}
              color="inherit"
              onClick={toggleLanguage}
            >
              <GTranslateIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            },
          }}
          variant="persistent"
          anchor="left"
          open={open}
        >
          <div>
            <DrawerHeader>
              <IconButton onClick={handleDrawerClose}>
                {theme.direction === "ltr" ? (
                  <ChevronLeftIcon />
                ) : (
                  <ChevronRightIcon />
                )}
              </IconButton>
            </DrawerHeader>
            <Divider />
            <List>
              {menuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => setScroll(item.scrollTo)}
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: theme.palette.primary.main,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        "& .MuiTypography-root": {
                          fontFamily: theme.typography.fontFamily,
                          fontSize: "0.9rem",
                          fontWeight: 500,
                          color: theme.palette.text.secondary,
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </div>

          <Toolbar sx={{ justifyContent: "center" }}></Toolbar>
          <Divider />
        </Drawer>
        <Main open={open}>
          <DrawerHeader />

          <DivMain
            scroll={scroll}
            mainRef={mainRef}
            isDarkMode={isDarkMode}
            onThemeToggle={handleThemeToggle}
          />
        </Main>
      </Box>
    </ThemeProvider>
  );
}

export default App;
